import {   
    PageHeader,
    Modal,
    Form,
    Input,
    DatePicker,
    Button as AntButton,
    message,
    Select,
} from 'antd'
import { Space, Button } from 'antd'
import { useQuery } from 'react-query'
import { useState, useEffect } from 'react'
import { getCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent } from '../../network/Calendar'
import { getUsers } from '../../network/Users'
import Loader from '../../components/Loader'
import Error from '../../components/Error'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'
import { ROLE_PERMISSIONS, ROLES, SCREEN_SIZE } from '../../constants/common'
import { useAuth } from '../../hooks/useAuth'
import { useQueryClient } from 'react-query'
import dayjs from 'dayjs'
import moment from 'moment'
import CalendarView from '../../components/Calendar'
import useIsMobile from '../../hooks/useIsMobile'

const Calendar = ({ history }) => {
  const { user } = useAuth()
  const permissions = ROLE_PERMISSIONS[user.role]
    ? ROLE_PERMISSIONS[user.role]
    : []

  const [form] = Form.useForm()
  const [detailForm] = Form.useForm()
  const queryClient = useQueryClient()
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [isEventDetailModalVisible, setIsEventDetailModalVisible] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(dayjs())
  const isMobile = useIsMobile(SCREEN_SIZE.MOBILE)
  
  // Get current store ID
  const currentStoreId = user?.currentStore

      // Fetch users from current store for create modal
  const storeUsers = useQuery(
    [
      'getUsers',
      {
        sort: [],
        range: [0, 1000],
        filter: {
          $and: [
            { 'stores.store': user.currentStore },
          ],
        }
      },
    ],
    getUsers,
    {
      enabled: !!currentStoreId && isCreateModalVisible,
      refetchOnWindowFocus: false,
    }
  )

  // Fetch users from current store for detail/edit modal
  const storeUsersForEdit = useQuery(
    [
      'getUsersForEdit',
      {
        sort: [],
        range: [0, 1000],
        filter: {
          $and: [
            { 'stores.store': user.currentStore },
          ],
        }
      },
    ],
    getUsers,
    {
      enabled: !!currentStoreId && (isEventDetailModalVisible && isEditMode),
      refetchOnWindowFocus: false,
    }
  )

  // Set initial values when modal opens for creating (not editing)
  useEffect(() => {
    if (isCreateModalVisible && currentStoreId && !isEditMode) {
      form.setFieldsValue({
        related_shop: currentStoreId,
        duration: 60, // Default 1 hour
      })
    }
  }, [isCreateModalVisible, currentStoreId, isEditMode, form])


// Build filter for current month or week (mobile)
const getMonthFilter = (month) => {
  const startOfMonth = month.startOf('month').startOf('day')
  const endOfMonth = month.endOf('month').endOf('day')
  
  const filter = {
    date_time_start: {
      $gte: startOfMonth.toISOString(),
      $lte: endOfMonth.toISOString(),
    }
  }
  
  // Add store filter if currentStoreId is available
  if (currentStoreId) {
    filter.related_shop = currentStoreId
  }
  
  return filter
}

// Build filter for mobile: 7-day window matching Calendar view (date through date+6)
const getMobileWindowFilter = (date) => {
  const startDay = date.startOf('day')
  const endDay = date.clone().add(6, 'day').endOf('day')
  
  const filter = {
    date_time_start: {
      $gte: startDay.toISOString(),
      $lte: endDay.toISOString(),
    }
  }
  
  if (currentStoreId) {
    filter.related_shop = currentStoreId
  }
  
  return filter
}

const calendarEvents = useQuery(
  [
    'getCalendarEvents',
    {
      sort: [],
      range: [0, 1000],
      filter: isMobile ? getMobileWindowFilter(currentMonth) : getMonthFilter(currentMonth),
      isMobile,
      currentStoreId, 
    },
  ],
  getCalendarEvents,
  {
    refetchOnWindowFocus: false,
  }
)


  const handleSubmitEvent = async (values) => {
    try {
      // Convert moment to ISO string for API
      // If date_time_end is not provided, set it to 1 hour after date_time_start
      let dateTimeEnd = null
      if (values.date_time_end) {
        dateTimeEnd = values.date_time_end.toISOString()
      } else if (values.date_time_start) {
        // Add 1 hour to date_time_start (default duration)
        dateTimeEnd = moment(values.date_time_start).add(1, 'hour').toISOString()
      }

      const eventData = {
        related_shop: values.related_shop,
        related_sales: values.related_sales,
        date_time_start: values.date_time_start ? values.date_time_start.toISOString() : null,
        date_time_end: dateTimeEnd,
        client_name: values.client_name,
        contact: values.contact || null,
        client_nationality: values.client_nationality || null,
        client_type: values.client_type || null,
        client_interests: values.client_interests || null,
        appointment_type: values.appointment_type,
        client_spending_power: values.client_spending_power || null,
        client_clothes_interests: values.client_clothes_interests || null,
      }

      if (isEditMode && selectedEvent?.id) {
        await updateCalendarEvent(selectedEvent.id, eventData)
        message.success('Event updated successfully')
      } else {
        await createCalendarEvent(eventData)
        message.success('Event created successfully')
      }

      form.resetFields()
      setIsCreateModalVisible(false)
      setIsEditMode(false)
      setSelectedEvent(null)
      // Invalidate and refetch calendar events to refresh the calendar
      await queryClient.invalidateQueries(['getCalendarEvents'])
      queryClient.refetchQueries(['getCalendarEvents'])
    } catch (error) {
      message.error(isEditMode ? 'Failed to update event' : 'Failed to create event')
      console.error('Error saving event:', error)
    }
  }

  const showCreateModal = () => {
    setIsEditMode(false)
    setSelectedEvent(null)
    setIsCreateModalVisible(true)
  }

  const handleCancelCreate = () => {
    setIsCreateModalVisible(false)
    setIsEditMode(false)
    setSelectedEvent(null)
    form.resetFields()
  }

  const handleEventClick = (event) => {
    setSelectedEvent(event)
    setIsEventDetailModalVisible(true)
  }

  const handleCloseEventDetail = () => {
    setIsEventDetailModalVisible(false)
    setIsEditMode(false)
    setSelectedEvent(null)
    detailForm.resetFields()
  }

  const handleEditEvent = () => {
    if (!selectedEvent) return
    
    if (!canEditOrDeleteEvent(selectedEvent)) {
      message.error('You do not have permission to edit this event')
      return
    }
    
    setIsEditMode(true)
    // Helper to safely convert date strings to moment (for Ant Design DatePicker)
    const parseDate = (dateString) => {
      if (!dateString) return undefined
      try {
        const parsed = moment(dateString)
        return parsed.isValid() ? parsed : undefined
      } catch (error) {
        console.error('Error parsing date:', dateString, error)
        return undefined
      }
    }

    // Calculate duration in minutes from start and end dates
    const startDate = parseDate(selectedEvent.date_time_start)
    const endDate = parseDate(selectedEvent.date_time_end)
    let duration = 60 // Default to 1 hour
    if (startDate && endDate) {
      const diffMinutes = moment(endDate).diff(moment(startDate), 'minutes')
      // Use exact duration if it matches valid options
      if ([30, 60, 90, 120].includes(diffMinutes)) {
        duration = diffMinutes
      }
    }

    const formValues = {
      related_shop: selectedEvent.related_shop?.id || selectedEvent.related_shop || currentStoreId,
      related_sales: selectedEvent.related_sales?.id || selectedEvent.related_sales,
      date_time_start: startDate,
      date_time_end: endDate,
      duration: duration,
      client_name: selectedEvent.client_name,
      contact: selectedEvent.contact || null,
      client_nationality: selectedEvent.client_nationality || null,
      client_type: selectedEvent.client_type || null,
      client_interests: selectedEvent.client_interests || null,
      appointment_type: selectedEvent.appointment_type,
      client_spending_power: selectedEvent.client_spending_power || null,
      client_clothes_interests: selectedEvent.client_clothes_interests || null,
    }
    
    setTimeout(() => {
      detailForm.setFieldsValue(formValues)
    }, 100)
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    detailForm.resetFields()
  }

  const handleDeleteEvent = () => {
    if (!selectedEvent?.id) return
    
    if (!canEditOrDeleteEvent(selectedEvent)) {
      message.error('You do not have permission to delete this event')
      return
    }
    
    Modal.confirm({
      title: 'Delete Event',
      content: 'Are you sure you want to delete this event? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteCalendarEvent(selectedEvent.id)
          message.success('Event deleted successfully')
          setIsEventDetailModalVisible(false)
          setSelectedEvent(null)
          // Invalidate and refetch calendar events to refresh the calendar
          await queryClient.invalidateQueries(['getCalendarEvents'])
          queryClient.refetchQueries(['getCalendarEvents'])
        } catch (error) {
          message.error('Failed to delete event')
          console.error('Error deleting event:', error)
        }
      },
    })
  }

  const handleUpdateEvent = async (values) => {
    if (!selectedEvent?.id) return
    
    // Check if user has permission to edit
    if (!canEditOrDeleteEvent(selectedEvent)) {
      message.error('You do not have permission to edit this event')
      setIsEditMode(false)
      return
    }
    
    try {
      // If date_time_end is not provided, set it to 1 hour after date_time_start
      let dateTimeEnd = null
      if (values.date_time_end) {
        dateTimeEnd = values.date_time_end.toISOString()
      } else if (values.date_time_start) {
        dateTimeEnd = moment(values.date_time_start).add(1, 'hour').toISOString()
      }

      const eventData = {
        related_shop: values.related_shop,
        related_sales: values.related_sales,
        date_time_start: values.date_time_start ? values.date_time_start.toISOString() : null,
        date_time_end: dateTimeEnd,
        client_name: values.client_name,
        contact: values.contact || null,
        client_nationality: values.client_nationality || null,
        client_type: values.client_type || null,
        client_interests: values.client_interests || null,
        appointment_type: values.appointment_type,
        client_spending_power: values.client_spending_power || null,
        client_clothes_interests: values.client_clothes_interests || null,
      }

      await updateCalendarEvent(selectedEvent.id, eventData)
      message.success('Event updated successfully')
      setIsEditMode(false)
      setIsEventDetailModalVisible(false)
      setSelectedEvent(null)
      detailForm.resetFields()
      // Invalidate and refetch calendar events to refresh the calendar
      await queryClient.invalidateQueries(['getCalendarEvents'])
      queryClient.refetchQueries(['getCalendarEvents'])
    } catch (error) {
      message.error('Failed to update event')
      console.error('Error updating event:', error)
    }
  }


  const canCreate = permissions.includes('can-create-calendar-events')

  const canEditOrDeleteEvent = (event) => {
    if (!event) return false
    
    if (user.role === ROLES.ADMIN || user.role === ROLES.SUPER_ADMIN) {
      return true
    }
    
    const createdBy = event.createdBy?.id || event.createdBy
    const currentUserId = user.id
    
    return createdBy === currentUserId
  }

  return (
    <>
      <PageHeader
        className="site-page-header"
        title="Calendar"
        onBack={() => {
          const url = createUrlWithFiltersFromCurrentURL('/home')
          history.push(url)
        }}
        extra={
          canCreate && (
            <Button type="primary" onClick={showCreateModal}>
              Create Event
            </Button>
          )
        }
        style={{ backgroundColor: '#fff' }}
      />

      {calendarEvents.isError ? (
        <Error retry={() => calendarEvents.refetch()} />
      ) : calendarEvents.isLoading ? (
        <Loader />
      ) : (
        <CalendarView 
          events={calendarEvents.data?.list || calendarEvents.data?.data || []} 
          onEventClick={handleEventClick}
          currentMonth={currentMonth}
          onMonthChange={setCurrentMonth}
        />
      )}
      <Modal
        title={isEditMode ? "Edit Event" : "Create New Event"}
        open={isCreateModalVisible}
        onCancel={handleCancelCreate}
        footer={null}
        wrapClassName="calendar-modal"
        width={600}
        bodyStyle={{ 
          maxHeight: '70vh', 
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingRight: '16px'
        }}
        className="calendar-modal"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitEvent}>
          <Form.Item 
            name="related_shop"
            hidden
          >
            <Input type="hidden" />
          </Form.Item>
          
          <Form.Item 
            label="Related Sales" 
            name="related_sales"
            rules={[{ required: true, message: 'Please select a sales person' }]}
          >
            <Select 
              placeholder="Select sales person"
              loading={storeUsers.isLoading}
              notFoundContent={storeUsers.isLoading ? <Loader /> : 'No users found'}
            >
              {storeUsers.data?.list?.map(user => (
                <Select.Option key={user.id} value={user.id}>
                  {user.name || user.email}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item 
            label="Date/Time Start" 
            name="date_time_start"
            rules={[{ required: true, message: 'Please select start date and time' }]}
          >
            <DatePicker 
              showTime={{ minuteStep: 30 }}
              format="YYYY-MM-DD HH:mm"
              popupClassName="calendar-modal-picker-dropdown"
              placeholder="Select start date and time" 
              style={{ width: '100%' }}
              inputReadOnly
              onChange={(date) => {
                const duration = form.getFieldValue('duration')
                if (date && duration) {
                  const endDate = moment(date).add(duration, 'minutes')
                  form.setFieldValue('date_time_end', endDate)
                }
              }}
            />
          </Form.Item>

          <Form.Item 
            label="Duration" 
            name="duration"
            rules={[{ required: true, message: 'Please select duration' }]}
          >
            <Select 
              placeholder="Select duration"
              style={{ width: '100%' }}
              onChange={(duration) => {
                const startDate = form.getFieldValue('date_time_start')
                if (startDate && duration) {
                  const endDate = moment(startDate).add(duration, 'minutes')
                  form.setFieldValue('date_time_end', endDate)
                }
              }}
            >
              <Select.Option value={30}>30 minutes</Select.Option>
              <Select.Option value={60}>1 hour</Select.Option>
              <Select.Option value={90}>1 hour 30 minutes</Select.Option>
              <Select.Option value={120}>2 hours</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item 
            name="date_time_end"
            hidden
          >
            <Input type="hidden" />
          </Form.Item>

          <Form.Item 
            label="Client name" 
            name="client_name"
            rules={[{ required: true, message: 'Please enter client name' }]}
          >
            <Input placeholder="Enter client name" />
          </Form.Item>

          <Form.Item
            label="Contact"
            name="contact"
          >
            <Input placeholder="Contact (optional)" />
          </Form.Item>

          <Form.Item 
            label="Client Nationality" 
            name="client_nationality"
            rules={[{ required: true, message: 'Please select client nationality' }]}
          >
            <Select placeholder="Select client nationality" >
              <Select.Option value="chinese">Chinese</Select.Option>
              <Select.Option value="foreigner">Foreigner</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item 
            label="Client Type" 
            name="client_type"
            rules={[{ required: true, message: 'Please select client type' }]}
          >
            <Select placeholder="Select client type">
              <Select.Option value="new">New</Select.Option>
              <Select.Option value="existing">Existing</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item 
            label="Client Interests" 
            name="client_interests"
            rules={[{ required: true, message: 'Please select client interests' }]}
          >
            <Select placeholder="Select client interests">
              <Select.Option value="business">Business</Select.Option>
              <Select.Option value="smart casual">Smart Casual</Select.Option>
              <Select.Option value="wedding">Wedding</Select.Option>
              <Select.Option value="woman">Woman</Select.Option>
              <Select.Option value="gift">Gift</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item 
            label="Appointment Type" 
            name="appointment_type"
            rules={[{ required: true, message: 'Please select appointment type' }]}
          >
            <Select placeholder="Select appointment type">
              <Select.Option value="discovery-inspiration">Discovery-Inspiration</Select.Option>
              <Select.Option value="free shirt">Free Shirt</Select.Option>
              <Select.Option value="new order">New Order</Select.Option>
              <Select.Option value="fitting">Fitting</Select.Option>
              <Select.Option value="adjustment">Adjustment</Select.Option>
              <Select.Option value="partner">Partner</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item 
            label="Client Spending Power" 
            name="client_spending_power"
            rules={[{ required: true, message: 'Please select client spending power' }]}
          >
            <Select placeholder="Select spending power">
              <Select.Option value="unknown">Unknown</Select.Option>
              <Select.Option value="average">Average</Select.Option>
              <Select.Option value="rich">Rich</Select.Option>
              <Select.Option value="very rich">Very Rich</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item 
            label="Client Clothes Interests" 
            name="client_clothes_interests"
            rules={[{ required: true, message: 'Please select client clothes interests' }]}
          >
            <Select placeholder="Select clothes interests">
              <Select.Option value="low price">Low Price</Select.Option>
              <Select.Option value="middle price">Middle Price</Select.Option>
              <Select.Option value="high price">High Price</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <AntButton type="primary" htmlType="submit">
                {isEditMode ? 'Update Event' : 'Create Event'}
              </AntButton>
              <AntButton onClick={handleCancelCreate}>Cancel</AntButton>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Event Detail/Edit Modal */}
      <Modal
        title={isEditMode ? "Edit Event" : "Event Details"}
        open={isEventDetailModalVisible}
        onCancel={handleCloseEventDetail}
        wrapClassName="calendar-modal"
        bodyStyle={{ 
          maxHeight: '70vh', 
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingRight: '16px'
        }}
        footer={isEditMode ? null : [
          <Button key="close" onClick={handleCloseEventDetail}>
            Close
          </Button>,
          canEditOrDeleteEvent(selectedEvent) && (
            <Button key="edit" type="primary" onClick={handleEditEvent}>
              Edit Event
            </Button>
          ),
          canEditOrDeleteEvent(selectedEvent) && (
            <Button key="delete" danger onClick={handleDeleteEvent}>
              Delete Event
            </Button>
          ),
        ].filter(Boolean)}
        width={600}
      >
        {selectedEvent && (
          isEditMode ? (
            <Form form={detailForm} layout="vertical" onFinish={handleUpdateEvent}>
              <Form.Item 
                name="related_shop"
                hidden
              >
                <Input type="hidden" />
              </Form.Item>
              
              <Form.Item 
                label="Related Sales" 
                name="related_sales"
                rules={[{ required: true, message: 'Please select a sales person' }]}
              >
                <Select 
                  placeholder="Select sales person"
                  loading={storeUsersForEdit.isLoading}
                  notFoundContent={storeUsersForEdit.isLoading ? <Loader /> : 'No users found'}
                >
                  {storeUsersForEdit.data?.list?.map(user => (
                    <Select.Option key={user.id} value={user.id}>
                      {user.name || user.email}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item 
                label="Date/Time Start" 
                name="date_time_start"
                rules={[{ required: true, message: 'Please select start date and time' }]}
              >
                <DatePicker 
                  showTime={{ minuteStep: 30 }}
                  format="YYYY-MM-DD HH:mm"
                  popupClassName="calendar-modal-picker-dropdown"
                  placeholder="Select start date and time" 
                  style={{ width: '100%' }}
                  inputReadOnly
                  onChange={(date) => {
                    const duration = detailForm.getFieldValue('duration')
                    if (date && duration) {
                      const endDate = moment(date).add(duration, 'minutes')
                      detailForm.setFieldValue('date_time_end', endDate)
                    }
                  }}
                />
              </Form.Item>

              <Form.Item 
                label="Duration" 
                name="duration"
                rules={[{ required: true, message: 'Please select duration' }]}
              >
                <Select 
                  placeholder="Select duration"
                  style={{ width: '100%' }}
                  onChange={(duration) => {
                    const startDate = detailForm.getFieldValue('date_time_start')
                    if (startDate && duration) {
                      const endDate = moment(startDate).add(duration, 'minutes')
                      detailForm.setFieldValue('date_time_end', endDate)
                    }
                  }}
                >
                  <Select.Option value={30}>30 minutes</Select.Option>
                  <Select.Option value={60}>1 hour</Select.Option>
                  <Select.Option value={90}>1 hour 30 minutes</Select.Option>
                  <Select.Option value={120}>2 hours</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item 
                name="date_time_end"
                hidden
              >
                <Input type="hidden" />
              </Form.Item>

              <Form.Item 
                label="Client name" 
                name="client_name"
                rules={[{ required: true, message: 'Please enter client name' }]}
              >
                <Input placeholder="Enter client name" />
              </Form.Item>

              <Form.Item
                label="Contact"
                name="contact"
              >
                <Input placeholder="Enter contact (optional)" />
              </Form.Item>

              <Form.Item 
                label="Client Nationality" 
                name="client_nationality"
              >
                <Select placeholder="Select client nationality">
                  <Select.Option value="chinese">Chinese</Select.Option>
                  <Select.Option value="foreigner">Foreigner</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item 
                label="Client Type" 
                name="client_type"
              >
                <Select placeholder="Select client type">
                  <Select.Option value="new">New</Select.Option>
                  <Select.Option value="existing">Existing</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item 
                label="Client Interests" 
                name="client_interests"
              >
                <Select placeholder="Select client interests">
                  <Select.Option value="business">Business</Select.Option>
                  <Select.Option value="smart casual">Smart Casual</Select.Option>
                  <Select.Option value="wedding">Wedding</Select.Option>
                  <Select.Option value="woman">Woman</Select.Option>
                  <Select.Option value="gift">Gift</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item 
                label="Appointment Type" 
                name="appointment_type"
                rules={[{ required: true, message: 'Please select appointment type' }]}
              >
                <Select placeholder="Select appointment type">
                  <Select.Option value="discovery-inspiration">Discovery-Inspiration</Select.Option>
                  <Select.Option value="free shirt">Free Shirt</Select.Option>
                  <Select.Option value="new order">New Order</Select.Option>
                  <Select.Option value="fitting">Fitting</Select.Option>
                  <Select.Option value="adjustment">Adjustment</Select.Option>
                  <Select.Option value="partner">Partner</Select.Option>
                  <Select.Option value="other">Other</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item 
                label="Client Spending Power" 
                name="client_spending_power"
              >
                <Select placeholder="Select spending power">
                  <Select.Option value="unknown">Unknown</Select.Option>
                  <Select.Option value="average">Average</Select.Option>
                  <Select.Option value="rich">Rich</Select.Option>
                  <Select.Option value="very rich">Very Rich</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item 
                label="Client Clothes Interests" 
                name="client_clothes_interests"
              >
                <Select placeholder="Select clothes interests">
                  <Select.Option value="low price">Low Price</Select.Option>
                  <Select.Option value="middle price">Middle Price</Select.Option>
                  <Select.Option value="high price">High Price</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item>
                <Space>
                  <AntButton type="primary" htmlType="submit">
                    Update Event
                  </AntButton>
                  <AntButton onClick={handleCancelEdit}>Cancel</AntButton>
                </Space>
              </Form.Item>
            </Form>
          ) : (
            <div style={{ padding: '16px 0' }}>
              <div style={{ marginBottom: '16px' }}>
                <strong>Client Name:</strong> {selectedEvent.client_name || 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>Contact:</strong> {selectedEvent.contact || 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>Shop:</strong> {selectedEvent.related_shop?.name?.en || selectedEvent.related_shop?.name?.zh || selectedEvent.related_shop?.name || selectedEvent.related_shop?.id || 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>Sales Person:</strong> {selectedEvent.related_sales?.name?.en || selectedEvent.related_sales?.name?.zh || selectedEvent.related_sales?.name || selectedEvent.related_sales?.email || selectedEvent.related_sales?.id || 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>Created By:</strong> {selectedEvent.createdBy?.name?.en || selectedEvent.createdBy?.name?.zh || selectedEvent.createdBy?.name || selectedEvent.createdBy?.email || selectedEvent.createdBy?.id || selectedEvent.createdBy || 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>Start Date/Time:</strong> {selectedEvent.date_time_start ? dayjs(selectedEvent.date_time_start).format('YYYY-MM-DD HH:mm') : 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>End Date/Time:</strong> {selectedEvent.date_time_end ? dayjs(selectedEvent.date_time_end).format('YYYY-MM-DD HH:mm') : 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>Appointment Type:</strong> {selectedEvent.appointment_type || 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>Client Nationality:</strong> {selectedEvent.client_nationality || 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>Client Type:</strong> {selectedEvent.client_type || 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>Client Interests:</strong> {selectedEvent.client_interests || 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>Client Spending Power:</strong> {selectedEvent.client_spending_power || 'N/A'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <strong>Client Clothes Interests:</strong> {selectedEvent.client_clothes_interests || 'N/A'}
              </div>
            </div>
          )
        )}
      </Modal>
    </>
  )
}

export default Calendar
